<template>
  <div
    class="column"
    style="width: 100%; max-width: 800px; margin-left: auto; margin-right: auto;"
  >
    <activity-date-and-time-card
      :activity-booking="activityBooking"
      :activity-sessions-and-available-entries="activitySessionsAndAvailableEntriesForActivityBooking.value"
      @select-date="handleSelectDate"
      @select-entry-time="handleSelectActivitySessionEntryTime"
      @select-activity-space="handleSelectActivitySpace"
    />
  </div>
</template>

<script>
import ActivityBooking from 'src/models/activity/ActivityBooking'
import ActivityDateAndTimeCard from 'src/modules/checkout/components/ActivityDateAndTimeCard'
import activitySessionResolver from 'src/services/activity-session/ActivitySessionResolver'
import { watchEffect } from 'vue'

export default {
  name: 'ActivitySelectDateAndTime',
  components: { ActivityDateAndTimeCard },
  inject: ['order', 'checkoutData'],
  props: {
    activityBooking: {
      type: ActivityBooking,
      required: true
    },
    swapSourceActivityBooking: {
      type: ActivityBooking,
      required: false,
      default: null
    },
    isQuickEdit: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  configuration: ['currentSite', 'activitySpaces'],
  data () {
    return {
      loading: false,
      hasPreSelectedSession: false
    }
  },
  models: {
    activitySessionsAndAvailableEntriesForActivityBooking: {
      variables () {
        return {
          activityBookingId: this.activityBooking.id,
          shouldRestrictToSamePriceCard: this.isQuickEdit,
          date: this.isQuickEdit ? this.activityBooking.config.selectedActivitySessionDate : null,
          skip: _.isNil(this.activityBooking.config.selectedActivitySessionDate) || this.activityBooking.loading
        }
      },
      immediate: true
    }
  },
  computed: {
    activeActivitySpaceId () {
      return this.activityBooking.activitySession?.activitySpace?.id || this.activityBooking.config.selectedActivitySpaceId
    }
  },
  watch: {
    'activitySessionsAndAvailableEntriesForActivityBooking.value': {
      handler (sessionsAndEntries) {
        // When sessions are loaded and we have a source booking, pre-select the same session
        if (!_.isEmpty(sessionsAndEntries) && this.swapSourceActivityBooking && !this.hasPreSelectedSession) {
          this.hasPreSelectedSession = true
          this.preSelectSameSession()
        }
      },
      immediate: true
    }
  },
  async mounted () {
    if (_.isNil(this.checkoutData)) return
    this.stopWatch = watchEffect(() => {
      this.checkoutData.showLoading = this.loading
      this.checkoutData.purchasingMembership = false
      this.checkoutData.stepTitle = this.$i18n.t('checkout.steps.dateAndTime')
      this.checkoutData.showActivityCard = true
      this.checkoutData.showCartControls = true
      this.checkoutData.showCheckoutFooter = true
      this.checkoutData.expandDefault = false
      this.checkoutData.expandActivityItems = false
      this.checkoutData.expandConcessions = false
      this.checkoutData.expandTickets = false
      this.checkoutData.expandActivityTickets = true
      this.checkoutData.infoTitle = this.$i18n.t('checkout.infoTitles.tickets')
      this.checkoutData.info = this.$i18n.tc('ticket.ticketCount', this.order.activityTicketOrderItems.length)
      this.checkoutData.showInfo = true
      this.checkoutData.showSubtotal = true
      this.checkoutData.showTotals = false
      this.checkoutData.showEmptyCartNextLabel = true
      this.checkoutData.canProceed = this.activityBooking.canBePlaced
    })
  },
  unmounted () {
    this.stopWatch?.()
  },
  methods: {
    async preSelectSameSession () {
      if (!this.swapSourceActivityBooking.activitySession) return

      this.loading = true

      try {
        // Sessions should already be loaded by the watcher
        const activitySessionsAndAvailableEntries = this.activitySessionsAndAvailableEntriesForActivityBooking?.value
        if (!activitySessionsAndAvailableEntries) return

        const { activitySession, activitySessionEntryTime, activitySessionExitTime } = activitySessionResolver.resolve(
          this.activityBooking,
          this.swapSourceActivityBooking.activitySessionEntryTime,
          activitySessionsAndAvailableEntries
        )

        this.activityBooking.activitySession = activitySession
        this.activityBooking.activitySessionEntryTime = activitySessionEntryTime
        this.activityBooking.activitySessionExitTime = activitySessionExitTime

        const sourceActivitySpaceId = this.swapSourceActivityBooking.activitySession?.activitySpace?.id
        if (sourceActivitySpaceId) {
          const activitySpace = this.activityBooking.allActivitySpaces(activitySessionsAndAvailableEntries).find(space => space.id === sourceActivitySpaceId)
          if (activitySpace && !this.activityBooking.isActivitySpaceDisabled(activitySpace, activitySessionsAndAvailableEntries)) {
            await this.activityBooking.toggleSelectedActivitySpace(activitySpace, activitySessionsAndAvailableEntries, !this.isQuickEdit)
          }
        }
      } finally {
        this.loading = false
      }
    },
    async handleSelectDate (selectedDate) {
      this.loading = true
      await this.activityBooking.selectActivitySessionDate(selectedDate, !this.isQuickEdit)
      await this.activitySessionsAndAvailableEntriesForActivityBooking.fetch(true)
      this.loading = false
    },
    handleSelectActivitySessionEntryTime (selectedEntryTime) {
      this.activityBooking.selectActivitySessionEntryTime(selectedEntryTime, this.activitySessionsAndAvailableEntriesForActivityBooking.value, !this.isQuickEdit)
    },
    handleSelectActivitySpace (activitySpace) {
      this.activityBooking.toggleSelectedActivitySpace(activitySpace, this.activitySessionsAndAvailableEntriesForActivityBooking.value, !this.isQuickEdit)
    }
  }
}
</script>
